/*
 * Authors: mariushirjanu, Triadi
 * Created: 08/11/2023
 */
import * as _ from 'underscore';
import { Component, EventEmitter, Output } from '@angular/core';
import { DefaultFormDialogComponent } from '../../../../shared/message/dialogs/default-form-dialog.component';
import { GaValidateService } from '../../../../shared/formly/ga-validate.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { Override } from '../../../../shared/app-decorators';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { BPProfilesDataService } from '../../bp-profiles-data.service';
import { BPProfilesAccreditationsDataService, IAccreditation, IBPProfileAccreditation } from './bp-profiles-accreditations-data.service';
import { ICompany } from '../../../../../type_definitions/datatypes';
import { BPProfilesService } from '../../bp-profiles.service';
import { BPProfilesAccreditationsService } from './bp-profiles-accreditations.service';

const ACCREDITATION_FIELD_PREFIX:string = 'acc_';

export interface IAddAccreditationsModel extends IBPProfileAccreditation {
  company:ICompany;
  [key:string]:any; // apart from existing properties add accreditation checkboxes prefixed with 'acc_'
}

@Component({
  selector: 'add-accreditations-dialog',
  templateUrl: '../../../../shared/message/dialogs/default-form-dialog.html'
})
export class AddAccreditationsDialogComponent extends DefaultFormDialogComponent {
  @Output() public onSaved:EventEmitter<IBPProfileAccreditation> = new EventEmitter<IBPProfileAccreditation>();
  @Output() public onSaving:EventEmitter<boolean> = new EventEmitter<boolean>();
  public model:IAddAccreditationsModel;
  public bpProfileId:number;

  private accreditations:IBPProfileAccreditation[];
  private vendorField:IExtraFieldConfigurationObject;
  private accreditationGroupField:IExtraFieldConfigurationObject;

  constructor(
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private bpProfilesService:BPProfilesService,
    private bpProfilesDataService:BPProfilesDataService,
    private bpProfilesAccreditationsService:BPProfilesAccreditationsService,
    private bpProfilesAccreditationsDataService:BPProfilesAccreditationsDataService,
    protected translationService:TranslationService,
    protected validateService:GaValidateService
  ) {
    super(validateService, translationService);
  }

  @Override(DefaultFormDialogComponent)
  public ngOnInit():void {
    super.ngOnInit();
    this.bpProfileId = this.bpProfilesService.activeProfile?.id;
    this.fields = this.createFields();
    this.saveButtonDisabled = true;
    this.fillFields();
    this.onVendorChange();
  }

  public save = ():void => {
    this.validateService.validateFields(this.fields, this.model)
      .then(() => {
        this.setSaveBusy = true;
        this.bpProfilesAccreditationsDataService.saveAccreditations(this.bpProfileId, this.model.company?.id, this.selectedAccreditations)
          .then(() => {
            // just notify browselist that the save took place (uses reload ALL rows)
            this.onSaved.emit();
          })
          .finally(() => {
            this.setSaveBusy = false;
          });
      });
  };

  private fillFields():void {
    this.formlyService.fillField(this.vendorField, this.bpProfilesDataService.getVendors());
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';

    return [
      this.vendorField = this.formlyService.aNgSelect('company', 'GA.GENERAL.VENDOR')
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withFormat(this.formattingService.formatCompany)
        .isRequired()
        .onChange(this.onVendorChange)
        .build(),

      this.accreditationGroupField = this.formlyService.aGroup('accreditationGroup')
        .withoutWrapper()
        .withWrapperClass(`${fullWidthClass} col-space-minus-top`)
        .withHideExpression(() => !this.model.company)
        .build()
    ];
  }

  // used when the vendor field is editable (when adding)
  private onVendorChange = (vendor?:ICompany):void => {
    const vendorId:number = vendor?.id;
    const accreditationType:string = this.bpProfilesAccreditationsService.accreditationType;
    if(vendorId) {
      this.loading = true;
      this.bpProfilesAccreditationsDataService.getVendorAccreditations(vendorId, this.bpProfileId, accreditationType)
        .then((accreditations:IAccreditation[]) => {
          this.accreditations = accreditations;
          this.accreditationGroupField.data.fields = [];
          _.each(accreditations, (accreditation:IAccreditation) => {
            const fieldKey:string = `${ACCREDITATION_FIELD_PREFIX}${accreditation.id.toString()}`;
            this.accreditationGroupField.data.fields.push(
              this.formlyService.aCheckbox(fieldKey, accreditation?.name)
                .withoutWrapper()
                .onChange(this.onAccreditationChange)
                .build()
            );

          });
        })
        .finally(() => {
          this.loading = false;
        });
    }
  };

  private get selectedAccreditations():IBPProfileAccreditation[] {
    return _.chain(this.accreditations)
      .filter((accreditation:IBPProfileAccreditation) => {
        const fieldKey:string = `${ACCREDITATION_FIELD_PREFIX}${accreditation.id.toString()}`;
        return this.model[ fieldKey ];
      })
      .value();
  }

  private onAccreditationChange = (active:boolean):void => {
    this.model.active = active;
    this.saveButtonDisabled = !this.selectedAccreditations?.length;
  };

  private set setSaveBusy(busy:boolean) {
    this.onSaving.emit(busy);
    this.saveButtonBusy = busy;
  }
}